import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from "react";
import { CaseSensitive } from "@fg/icons";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { normalizeLinkAddress } from "./richDom";
import { NO_FORMATS, RichEditor, type ActiveFormats, type RichCommand, type RichEditorHandle, type RichEditorProps } from "./RichEditor";
import { RichToolbar, ToolButton } from "./RichToolbar";

const TOOLBAR_KEY = "fg.connect.richToolbar";
const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

/** Collapsed by default, as in Teams: the box is one clean line until the Format button is pressed. */
function readToolbarPref(): boolean {
    try { return window.localStorage.getItem(TOOLBAR_KEY) === "on"; } catch { return false; }
}

function writeToolbarPref(on: boolean): void {
    try { window.localStorage.setItem(TOOLBAR_KEY, on ? "on" : "off"); } catch { /* private mode: the choice lasts for this page only */ }
}

export interface RichInputProps extends RichEditorProps {
    /** Above the toolbar: attached files. */
    header?: ReactNode;
    /** On the right of the line, after the Format button: emoji, attach, poll ... */
    footerLeft?: ReactNode;
    /** On the right of the line, last: Send (or Save / Cancel). */
    footerRight?: ReactNode;
    /** Clears the box already; this is for the caller's own state (attachments, importance). */
    onDiscard?: () => void;
    cardClassName?: string;
}

function sameFormats(a: ActiveFormats, b: ActiveFormats): boolean {
    return (Object.keys(a) as (keyof ActiveFormats)[]).every((k) => a[k] === b[k]);
}

/**
 * The Teams-style composer card: one line of text with the Format, emoji, attach ... Send buttons on its right.
 * The Format button (or Ctrl+Shift+X) opens the formatting toolbar above the text and closes it again; the
 * choice is remembered and the typed text and formatting are never lost by it.
 */
export const RichInput = forwardRef<RichEditorHandle, RichInputProps>(function RichInput(
    { header, footerLeft, footerRight, onDiscard, cardClassName, onSelectionChange, onRequestLink, ...editorProps }, ref
) {
    const editor = useRef<RichEditorHandle>(null);
    useImperativeHandle(ref, () => editor.current as RichEditorHandle, []);
    const [showToolbar, setShowToolbar] = useState(readToolbarPref);
    const [formats, setFormats] = useState<ActiveFormats>(NO_FORMATS);
    const raf = useRef<number | undefined>(undefined);

    const refresh = useCallback(() => {
        if (raf.current !== undefined) window.cancelAnimationFrame(raf.current);
        raf.current = window.requestAnimationFrame(() => {
            const next = editor.current?.activeFormats() ?? NO_FORMATS;
            setFormats((prev) => (sameFormats(prev, next) ? prev : next));
        });
    }, []);
    useEffect(() => () => { if (raf.current !== undefined) window.cancelAnimationFrame(raf.current); }, []);

    const [link, setLink] = useState<{ text: string; href: string; existing: boolean } | null>(null);
    const openLink = useCallback(() => {
        const ctx = editor.current?.beginLink();
        if (ctx) setLink(ctx);
    }, []);

    const toggleToolbar = () => {
        const next = !showToolbar;
        setShowToolbar(next);
        writeToolbarPref(next);
        editor.current?.focus();
    };

    const run = (cmd: RichCommand) => {
        editor.current?.run(cmd);
        refresh();
    };

    return (
        <div
            className={cn("rounded-lg border border-[var(--connect-line-strong)] bg-card focus-within:border-primary focus-within:ring-1 focus-within:ring-primary", cardClassName)}
            onKeyDownCapture={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.shiftKey && !e.altKey && e.key.toLowerCase() === "x") {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleToolbar();
                }
            }}
        >
            {header}
            {showToolbar ? (
                <RichToolbar
                    formats={formats}
                    disabled={editorProps.disabled}
                    onRun={run}
                    onLink={openLink}
                    onDiscard={() => { editor.current?.clear(); onDiscard?.(); editor.current?.focus(); refresh(); }}
                />
            ) : null}
            <div className="flex items-end gap-1 px-2">
                <div className="flex min-w-0 flex-1">
                    <RichEditor
                        ref={editor}
                        {...editorProps}
                        onSelectionChange={() => { refresh(); onSelectionChange?.(); }}
                        onRequestLink={() => { openLink(); onRequestLink?.(); }}
                    />
                </div>
                <div className="mb-1 flex shrink-0 items-center gap-0.5">
                    <ToolButton
                        label="Format"
                        shortcut={`${isMac ? "⌘⇧" : "Ctrl+Shift+"}X`}
                        pressed={showToolbar}
                        onClick={toggleToolbar}
                    >
                        <CaseSensitive className="h-5 w-5" />
                    </ToolButton>
                    {footerLeft}
                    {footerRight}
                </div>
            </div>
            <LinkDialog
                state={link}
                onClose={() => { setLink(null); window.setTimeout(() => editor.current?.focus(), 0); }}
                onSubmit={(text, href) => { editor.current?.commitLink(text, href); setLink(null); refresh(); }}
                onRemove={() => { setLink(null); editor.current?.run({ type: "unlink" }); refresh(); }}
            />
        </div>
    );
});

function LinkDialog({ state, onClose, onSubmit, onRemove }: {
    state: { text: string; href: string; existing: boolean } | null;
    onClose: () => void;
    onSubmit: (text: string, href: string) => void;
    onRemove: () => void;
}) {
    const [text, setText] = useState("");
    const [href, setHref] = useState("");
    const [error, setError] = useState<string | null>(null);
    useEffect(() => {
        if (state) { setText(state.text); setHref(state.href); setError(null); }
    }, [state]);

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!normalizeLinkAddress(href)) {
            setError("Enter a web address (https://…) or an email address.");
            return;
        }
        onSubmit(text, href);
    };

    const input = "h-9 w-full rounded-md border bg-background px-2 text-sm outline-none focus:ring-2 focus:ring-primary";
    return (
        <Dialog open={state !== null} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" onCloseAutoFocus={(e) => e.preventDefault()}>
                <DialogHeader>
                    <DialogTitle>{state?.existing ? "Edit link" : "Insert link"}</DialogTitle>
                    <DialogDescription>Text to show, and where the link goes.</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="grid gap-3">
                    <label className="grid gap-1 text-sm">
                        <span className="font-medium">Text to display</span>
                        <input value={text} onChange={(e) => setText(e.target.value)} className={input} autoComplete="off" />
                    </label>
                    <label className="grid gap-1 text-sm">
                        <span className="font-medium">Address</span>
                        <input
                            value={href}
                            onChange={(e) => { setHref(e.target.value); setError(null); }}
                            className={input}
                            placeholder="https://"
                            inputMode="url"
                            autoComplete="off"
                            autoFocus
                            aria-invalid={error !== null}
                        />
                    </label>
                    {error ? <p role="alert" className="text-xs text-destructive">{error}</p> : null}
                    <DialogFooter className="gap-2 sm:gap-0">
                        {state?.existing ? (
                            <button type="button" onClick={onRemove} className="h-9 rounded-md border px-3 text-sm hover:bg-accent sm:mr-auto">Remove link</button>
                        ) : null}
                        <button type="button" onClick={onClose} className="h-9 rounded-md border px-3 text-sm hover:bg-accent">Cancel</button>
                        <button type="submit" className="h-9 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">
                            {state?.existing ? "Update" : "Insert"}
                        </button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
